import { useForm } from "@tanstack/react-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { useState, type ReactNode } from "react";
import { z } from "zod";

import type {
	MfaRequiredResponse,
	PasswordResetCodeSentResponse,
	Session,
	SessionOpenedResponse,
} from "@ravnar/api-contract";

import { confirmPasswordReset, requestPasswordReset, sessionQueryOptions, startSession } from "../api/session";
import { MfaCodeForm } from "../components/mfa-code-form";
import { MfaSetupStep } from "../components/mfa-setup-step";
import { PageHeader } from "../components/shell/app-shell";
import { PublicPage } from "../components/shell/public-page";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Field, Input } from "../components/ui/field";
import { LOCAL_CONFIRMATION_CODE } from "../demo-data/organization";
import { fieldErrorMessage } from "../lib/form";

const requestCodeFormSchema = z.object({ email: z.email("Enter a valid email address") });

const chooseNewPasswordFormSchema = z
	.object({
		code: z
			.string()
			.trim()
			.regex(/^\d{6}$/, "Enter the 6-digit code from the email"),
		newPassword: z.string().min(12, "Use at least 12 characters").max(128, "Use at most 128 characters"),
		confirmPassword: z.string(),
	})
	.refine(value => value.confirmPassword === value.newPassword, {
		message: "The passwords do not match",
		path: ["confirmPassword"],
	});

export const Route = createFileRoute("/forgot-password")({
	// Someone who is signed in already knows their password.
	beforeLoad: async ({ context }) => {
		const session = await context.queryClient.query(sessionQueryOptions());
		if (session !== null) {
			throw redirect({ to: "/" });
		}
	},
	head: () => ({ meta: [{ title: "Reset password · Ravnar" }] }),
	staticData: { public: true },
	component: ForgotPasswordPage,
});

function ForgotPasswordPage() {
	const navigate = Route.useNavigate();
	const queryClient = useQueryClient();
	// Set once the API has emailed a code, whether or not the address actually has an account.
	const [sent, setSent] = useState<PasswordResetCodeSentResponse | undefined>();
	// Set once the API asks a member who reset their password and has an authenticator app for its code.
	const [mfaChallenge, setMfaChallenge] = useState<MfaRequiredResponse | undefined>();
	// Set once the API asks a member whose organization requires a factor, and who has none, to set one up.
	const [mfaSetup, setMfaSetup] = useState<{ readonly email: string; readonly password: string } | undefined>();

	const continueSignedIn = async (session: Session) => {
		startSession(queryClient, session);
		await navigate({ to: "/", replace: true });
	};

	let step: ReactNode;
	if (mfaChallenge !== undefined) {
		step = (
			<MfaCodeForm
				challenge={mfaChallenge}
				onSignedIn={continueSignedIn}
				onStartAgain={() => {
					setMfaChallenge(undefined);
					setSent(undefined);
				}}
			/>
		);
	} else if (mfaSetup !== undefined) {
		step = (
			<MfaSetupStep
				email={mfaSetup.email}
				password={mfaSetup.password}
				onSignedIn={continueSignedIn}
				onStartAgain={() => {
					setMfaSetup(undefined);
					setSent(undefined);
				}}
			/>
		);
	} else if (sent === undefined) {
		step = <RequestCodeForm onSent={setSent} />;
	} else {
		step = (
			<ChooseNewPasswordForm
				sent={sent}
				onSignedIn={continueSignedIn}
				onMfaRequired={setMfaChallenge}
				onMfaSetupRequired={setMfaSetup}
				onStartAgain={() => {
					setSent(undefined);
				}}
			/>
		);
	}

	return (
		<PublicPage>
			<Card className="p-6">{step}</Card>
		</PublicPage>
	);
}

interface RequestCodeFormProps {
	readonly onSent: (sent: PasswordResetCodeSentResponse) => void;
}

/** The first step: asks for the email a reset code should go to. The answer never says whether it has an account. */
function RequestCodeForm({ onSent }: RequestCodeFormProps) {
	const mutation = useMutation({ mutationFn: requestPasswordReset });

	const form = useForm({
		defaultValues: { email: "" },
		// Zod schemas are Standard Schema validators, so no adapter is needed.
		validators: { onChange: requestCodeFormSchema, onSubmit: requestCodeFormSchema },
		onSubmit: async ({ value }) => {
			let response: PasswordResetCodeSentResponse;
			try {
				response = await mutation.mutateAsync(value);
			} catch {
				// The alert below shows mutation.error. Rethrowing would make handleSubmit reject unhandled.
				return;
			}
			onSent(response);
		},
	});

	return (
		<>
			<PageHeader
				title="Forgot your password?"
				description="Enter your work email and we will send you a code."
			/>
			<form
				noValidate
				className="flex flex-col gap-4"
				onSubmit={event => {
					event.preventDefault();
					event.stopPropagation();
					void form.handleSubmit();
				}}
			>
				<form.Field name="email">
					{field => (
						<Field label="Email" error={fieldErrorMessage(field.state.meta)}>
							<Input
								type="email"
								name={field.name}
								autoComplete="username"
								value={field.state.value}
								onBlur={field.handleBlur}
								onChange={event => {
									field.handleChange(event.target.value);
								}}
							/>
						</Field>
					)}
				</form.Field>
				{mutation.isError ? (
					<p role="alert" className="text-caption text-alert">
						{mutation.error.message}
					</p>
				) : null}
				<form.Subscribe selector={state => [state.canSubmit, state.isSubmitting]}>
					{([canSubmit, isSubmitting]) => (
						<Button
							type="submit"
							variant="primary"
							className="w-full justify-center"
							disabled={!canSubmit || isSubmitting}
						>
							{isSubmitting ? "Sending…" : "Send reset code"}
						</Button>
					)}
				</form.Subscribe>
			</form>
			<p className="mt-5 border-t border-line pt-4 text-center text-body text-ink-2">
				<Link to="/login" className="font-medium text-accent hover:underline">
					Back to sign in
				</Link>
			</p>
		</>
	);
}

interface ChooseNewPasswordFormProps {
	readonly sent: PasswordResetCodeSentResponse;
	readonly onSignedIn: (session: Session) => Promise<void>;
	readonly onMfaRequired: (challenge: MfaRequiredResponse) => void;
	readonly onMfaSetupRequired: (credentials: { readonly email: string; readonly password: string }) => void;
	readonly onStartAgain: () => void;
}

/**
 * The second step: the emailed code and the password to set. Confirming it signs the member in, or, for a member with
 * an authenticator app, asks for its code before it does, or, for a member whose organization requires a factor they
 * do not have, walks them through setting one up.
 */
function ChooseNewPasswordForm({
	sent,
	onSignedIn,
	onMfaRequired,
	onMfaSetupRequired,
	onStartAgain,
}: ChooseNewPasswordFormProps) {
	const mutation = useMutation({ mutationFn: confirmPasswordReset });

	const form = useForm({
		defaultValues: { code: "", newPassword: "", confirmPassword: "" },
		// Zod schemas are Standard Schema validators, so no adapter is needed.
		validators: { onChange: chooseNewPasswordFormSchema, onSubmit: chooseNewPasswordFormSchema },
		onSubmit: async ({ value }) => {
			// parse() applies the schema's trimming, so the code's surrounding spaces are not sent.
			const { code, newPassword } = chooseNewPasswordFormSchema.parse(value);
			let response: SessionOpenedResponse | MfaRequiredResponse;
			try {
				response = await mutation.mutateAsync({ email: sent.email, code, newPassword });
			} catch {
				// The alert below shows mutation.error. Rethrowing would make handleSubmit reject unhandled.
				return;
			}
			if (response.status === "mfa-required") {
				onMfaRequired(response);
				return;
			}
			if (response.status === "mfa-setup-required") {
				onMfaSetupRequired({ email: response.email, password: newPassword });
				return;
			}
			await onSignedIn(response.session);
		},
	});

	return (
		<>
			<PageHeader
				title="Choose a new password"
				description={`Enter the 6-digit code we emailed to ${sent.email} and the password you want to use.`}
			/>
			<form
				noValidate
				className="flex flex-col gap-4"
				onSubmit={event => {
					event.preventDefault();
					event.stopPropagation();
					void form.handleSubmit();
				}}
			>
				<form.Field name="code">
					{field => (
						<Field label="Code" error={fieldErrorMessage(field.state.meta)}>
							<Input
								name={field.name}
								inputMode="numeric"
								autoComplete="one-time-code"
								value={field.state.value}
								onBlur={field.handleBlur}
								onChange={event => {
									field.handleChange(event.target.value);
								}}
							/>
						</Field>
					)}
				</form.Field>
				{/* The local API that development runs against emails nothing and always expects this code. */}
				{import.meta.env.DEV ? (
					<p className="text-caption text-ink-3">Local API code: {LOCAL_CONFIRMATION_CODE}</p>
				) : null}
				<form.Field name="newPassword">
					{field => (
						<Field label="New password" error={fieldErrorMessage(field.state.meta)}>
							<Input
								type="password"
								name={field.name}
								autoComplete="new-password"
								value={field.state.value}
								onBlur={field.handleBlur}
								onChange={event => {
									field.handleChange(event.target.value);
								}}
							/>
						</Field>
					)}
				</form.Field>
				<form.Field name="confirmPassword">
					{field => (
						<Field label="Confirm new password" error={fieldErrorMessage(field.state.meta)}>
							<Input
								type="password"
								name={field.name}
								autoComplete="new-password"
								value={field.state.value}
								onBlur={field.handleBlur}
								onChange={event => {
									field.handleChange(event.target.value);
								}}
							/>
						</Field>
					)}
				</form.Field>
				<p className="text-caption text-ink-3">Use at least 12 characters.</p>
				{mutation.isError ? (
					<p role="alert" className="text-caption text-alert">
						{mutation.error.message}
					</p>
				) : null}
				<form.Subscribe selector={state => [state.canSubmit, state.isSubmitting]}>
					{([canSubmit, isSubmitting]) => (
						<Button
							type="submit"
							variant="primary"
							className="w-full justify-center"
							disabled={!canSubmit || isSubmitting}
						>
							{isSubmitting ? "Setting…" : "Set password and sign in"}
						</Button>
					)}
				</form.Subscribe>
			</form>
			<p className="mt-5 border-t border-line pt-4 text-center text-body text-ink-2">
				<button
					type="button"
					className="cursor-pointer font-medium text-accent hover:underline"
					onClick={onStartAgain}
				>
					Start again
				</button>
			</p>
		</>
	);
}
